import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import auth from './AuthService';
import { TimePenaltyChart, GLOBAL_TIME_PENALTY_DEFAULTS } from './TimePenaltyChart';
import { TeePenaltyTable, GLOBAL_TEE_PENALTY_DEFAULTS } from './TeePenaltyTable';
import { PenaltyHeatmap } from './PenaltyHeatmap';
import SyncProgress from './BotStatus/SyncProgress';

function EditProfile() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    username: auth.getProfile()?.username || '',
    email: '',
    password: '',
    golfBookingsLogin: '',
    golfBookingsPassword: '',
    friends: [],
    peopleToAvoid: [],
    newFriend: '',
    newAvoid: '',
    timePenaltyTable: null,
    teePenaltyTable: [],
    bookingName: '',
  });
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({ heatmap: true, time: true, tee: true });

  const toggleSection = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await auth.fetch('/accounts/profile/', { method: 'GET' });
        setState((prev) => ({
          ...prev,
          email: data.email || '',
          bookingName: data.bookingName || '',
          golfBookingsLogin: data.golfBookingsLogin || '',
          friends: data.friends || [],
          peopleToAvoid: data.peopleToAvoid || [],
          timePenaltyTable: data.timePenaltyTable && data.timePenaltyTable.length > 0 ? data.timePenaltyTable : null,
          teePenaltyTable: data.teePenaltyTable || null,
        }));
      } catch (err) {
        console.error('Fetch profile failed:', err);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const addFriend = () => {
    const { newFriend, friends } = state;
    if (newFriend && !friends.includes(newFriend)) {
      setState((prev) => ({ ...prev, friends: [...friends, newFriend], newFriend: '' }));
    }
  };

  const addAvoid = () => {
    const { newAvoid, peopleToAvoid } = state;
    if (newAvoid && !peopleToAvoid.includes(newAvoid)) {
      setState((prev) => ({
        ...prev,
        peopleToAvoid: [...peopleToAvoid, newAvoid],
        newAvoid: '',
      }));
    }
  };

  const removeFriend = (friend) => {
    setState((prev) => ({
      ...prev,
      friends: prev.friends.filter((f) => f !== friend),
    }));
  };

  const removeAvoid = (person) => {
    setState((prev) => ({
      ...prev,
      peopleToAvoid: prev.peopleToAvoid.filter((p) => p !== person),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { email, password, golfBookingsLogin, golfBookingsPassword, friends, peopleToAvoid, timePenaltyTable, teePenaltyTable, bookingName } = state;
      const updateData = { friends, peopleToAvoid };
      if (email) updateData.email = email;
      if (password) updateData.password = password;
      if (golfBookingsLogin) updateData.golfBookingsLogin = golfBookingsLogin;
      if (golfBookingsPassword) updateData.golfBookingsPassword = golfBookingsPassword;
      if (bookingName) updateData.bookingName = bookingName;
      // timePenaltyTable: null = use defaults, array = full override
      updateData.timePenaltyTable = timePenaltyTable && timePenaltyTable.length > 0 ? timePenaltyTable : null;
      // teePenaltyTable: null = use defaults, array = per-tee overrides
      updateData.teePenaltyTable = teePenaltyTable && teePenaltyTable.length > 0 ? teePenaltyTable : null;
      await auth.fetch('/accounts/update-profile/', {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      alert('Profile updated successfully');
      navigate('/');
    } catch (err) {
      alert('Update failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <div className="container">
      <h3>Edit Profile</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Username (read-only):</label>
          <input type="text" className="form-control" value={state.username} disabled />
        </div>
        <div className="form-group my-2">
          <label>Email:</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={state.email}
            onChange={handleChange}
          />
        </div>
        <div className="form-group my-2">
          <label>Player Name:</label>
          <input
            type="text"
            name="bookingName"
            className="form-control"
            value={state.bookingName}
            onChange={handleChange}
            placeholder="e.g. John Smith"
          />
        </div>
        <div className="form-group my-2">
          <label>Password:</label>
          <input
            type="password"
            name="password"
            className="form-control"
            value={state.password}
            onChange={handleChange}
            placeholder="Leave blank to keep unchanged"
          />
        </div>
        <div className="form-group my-2">
          <label>Golf Website Login:</label>
          <input
            type="text"
            name="golfBookingsLogin"
            className="form-control"
            value={state.golfBookingsLogin}
            onChange={handleChange}
          />
        </div>
        <div className="form-group my-2">
          <label>Golf Website Password:</label>
          <input
            type="password"
            name="golfBookingsPassword"
            className="form-control"
            value={state.golfBookingsPassword}
            onChange={handleChange}
            placeholder="Leave blank to keep unchanged"
          />
        </div>
        <div className="form-group my-2">
          <label>Friends:</label>
          <ul>
            {state.friends.map((friend) => (
              <li key={friend} className="my-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm me-2"
                  onClick={() => removeFriend(friend)}
                >
                  Remove
                </button>
                {friend}
              </li>
            ))}
          </ul>
          <div className="d-flex">
            <input
              type="text"
              className="form-control me-2"
              name="newFriend"
              value={state.newFriend}
              onChange={handleChange}
              placeholder="Add friend"
            />
            <button type="button" className="btn btn-outline-secondary" onClick={addFriend}>
              Add
            </button>
          </div>
        </div>
        <div className="form-group my-2">
          <label>People to Avoid:</label>
          <ul>
            {state.peopleToAvoid.map((person) => (
              <li key={person} className="my-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm me-2"
                  onClick={() => removeAvoid(person)}
                >
                  Remove
                </button>
                {person}
              </li>
            ))}
          </ul>
          <div className="d-flex">
            <input
              type="text"
              className="form-control me-2"
              name="newAvoid"
              value={state.newAvoid}
              onChange={handleChange}
              placeholder="Add person to avoid"
            />
            <button type="button" className="btn btn-outline-secondary" onClick={addAvoid}>
              Add
            </button>
          </div>
        </div>

        <hr className="my-4" />
        <div className="d-flex justify-content-between align-items-center mb-0" style={{ cursor: 'pointer' }} onClick={() => toggleSection('heatmap')}>
          <h5 className="mb-0">Penalty Heatmap <small className="text-secondary small">— visualize combined penalties</small></h5>
          <span className="fs-5" style={{ fontWeight: 'bold', minWidth: 20 }}>{collapsed.heatmap ? '+' : '−'}</span>
        </div>
        <div style={{ display: collapsed.heatmap ? 'none' : 'block', marginTop: 8 }}>
          <PenaltyHeatmap
            timePenaltyTable={state.timePenaltyTable || [...GLOBAL_TIME_PENALTY_DEFAULTS]}
            teePenaltyTable={state.teePenaltyTable || []}
          />
        </div>

        <hr className="my-4" />
        <div className="d-flex justify-content-between align-items-center mb-0" style={{ cursor: 'pointer' }} onClick={() => toggleSection('time')}>
          <h5 className="mb-0">Time Penalty Configuration <small className="text-secondary small">— drag points to adjust</small></h5>
          <span className="fs-5" style={{ fontWeight: 'bold', minWidth: 20 }}>{collapsed.time ? '+' : '−'}</span>
        </div>
        <div style={{ display: collapsed.time ? 'none' : 'block', marginTop: 8 }}>
          <p className="text-muted small">
            Drag points to adjust penalty values. Higher penalty = lower priority.
            This replaces the default table entirely when you save.
          </p>
          <TimePenaltyChart
            value={state.timePenaltyTable || [...GLOBAL_TIME_PENALTY_DEFAULTS]}
            onChange={(val) => setState((prev) => ({ ...prev, timePenaltyTable: val }))}
            defaults={GLOBAL_TIME_PENALTY_DEFAULTS}
          />
        </div>

        <hr className="my-4" />
        <div className="d-flex justify-content-between align-items-center mb-0" style={{ cursor: 'pointer' }} onClick={() => toggleSection('tee')}>
          <h5 className="mb-0">Tee Penalty Configuration <small className="text-secondary small">— override per-tee scores</small></h5>
          <span className="fs-5" style={{ fontWeight: 'bold', minWidth: 20 }}>{collapsed.tee ? '+' : '−'}</span>
        </div>
        <div style={{ display: collapsed.tee ? 'none' : 'block', marginTop: 8 }}>
          <p className="text-muted small">
            Override specific tee scores. Tees without overrides use the default values.
          </p>
          <TeePenaltyTable
            value={state.teePenaltyTable || []}
            onChange={(val) => setState((prev) => ({ ...prev, teePenaltyTable: val }))}
            defaults={GLOBAL_TEE_PENALTY_DEFAULTS}
          />
        </div>

        <SyncProgress active={saving} progress={50} message="Saving profile..." />
        <button type="button" className="btn btn-secondary me-2 my-2" onClick={handleCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary my-2">
          Save
        </button>
      </form>
    </div>
  );
}

export default EditProfile;